import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Form, Input, Select, Space, Typography, Tooltip, InputNumber, Calendar, Button, Modal } from 'antd';
import { LeftOutlined, RightOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { SuffixIcon } from '@med-fe/ui';
import { api } from '@med-fe/util';
import { StyledParamsForm, StyledFormItemName, StyledFormItem, StyledPrimaryButton } from '../../../common/styled';
import { useMainInfoCreate } from './useMainInfoCreate';
import { useMainInfoEdit } from './useMainInfoEdit';
import { useSelector, useDispatch } from 'react-redux';
import { getEditedExam, getAddedExam } from '../../templates-selector';
import { fetchLiveRangeTemplateDetails } from '../../templates-actions';
import moment, { Moment } from 'moment';

// Interface for Live Range data items
export interface LiveRangeDataItem {
  startDate: string;
  templateId: number | string;
  rangeNo: number;
  employeeId: number | string;
  office: number | string;
}

export const MainInfo = ({
  isClone = false,
  isCreate = false,
  isDraft = false,
  onUpdateSuccess,
  overrideCloneName,
  fromLiveRanges = false,
  rangeType = 'default',
  isSpecialRange = false,
  isRange0 = false,
  liveRangeData = [],
  initialTemplateId,
}: {
  isClone?: boolean;
  isCreate?: boolean;
  isDraft?: boolean;
  onUpdateSuccess?: () => void;
  overrideCloneName?: boolean;
  fromLiveRanges?: boolean;
  rangeType?: string;
  isSpecialRange?: boolean;
  isRange0?: boolean;
  liveRangeData?: LiveRangeDataItem[];
  initialTemplateId?: string;
}) => {
  const [form] = Form.useForm();
  const { Text } = Typography;
  const dispatch = useDispatch();

  const mainInfoCreate = useMainInfoCreate(form);
  const mainInfoEdit = useMainInfoEdit(form, {
    onUpdateSuccess,
    overrideCloneName,
    fromLiveRanges,
    rangeType,
    isSpecialRange,
    isRange0,
  });
  // Selectors for edit/add state — must be called unconditionally (hook rules)
  const editedExam = useSelector(getEditedExam);
  const addedExam = useSelector(getAddedExam);

  // State for selected date in calendar (for special range)
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Moment | null>(null);

  // State for calendar display month (separate from selected date)
  const [calendarDisplayMonth, setCalendarDisplayMonth] = useState<Moment>(moment());

  // State for switch date confirmation modal
  const [switchDateModalVisible, setSwitchDateModalVisible] = useState(false);
  const [pendingSwitchDate, setPendingSwitchDate] = useState<{ date: Moment; matchingRange: LiveRangeDataItem } | null>(
    null
  );

  // Local state for liveRangeData that can be updated on month navigation
  const [localLiveRangeData, setLocalLiveRangeData] = useState<LiveRangeDataItem[]>(liveRangeData);

  // Track if initial fetch has been done
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    setLocalLiveRangeData(liveRangeData);
  }, [liveRangeData]);

  // Counter to force calendar re-render when data changes
  const [calendarKey, setCalendarKey] = useState(0);

  // Increment calendar key when localLiveRangeData changes to force re-render
  useEffect(() => {
    setCalendarKey((prev) => prev + 1);
  }, [localLiveRangeData]);

  // Function to fetch live range data for a specific month
  const fetchLiveRangeDataForMonth = useCallback(
    async (targetMonth: Moment) => {
      if (!isSpecialRange) return;
      // Get employeeId and office - try multiple sources
      const employeeId = localLiveRangeData[0]?.employeeId;
      const office = localLiveRangeData[0]?.office;

      if (!employeeId || !office) {
        return;
      }

      // Calculate start date: first day of month or today's date, whichever is later
      const monthStart = targetMonth.clone().startOf('month');
      const today = moment().startOf('day');
      const apiStartDate = monthStart.isBefore(today) ? today.format('YYYY-MM-DD') : monthStart.format('YYYY-MM-DD');

      // Calculate page number: difference between target month and current month
      const currentMonth = moment().month(); // 0-indexed (April = 3)
      const targetMonthNum = targetMonth.month(); // 0-indexed
      const currentYear = moment().year();
      const targetYear = targetMonth.year();
      const pageNo = (targetYear - currentYear) * 12 + (targetMonthNum - currentMonth);

      try {
        const payload = {
          page: pageNo,
          size: 32,
          employeeId: employeeId,
          office: office,
          startDate: apiStartDate,
        };

        const response: any = await api.post(
          `/live-ranges/mapped-special?page=${pageNo}&size=32`,
          payload,
          null,
          true,
          false
        );

        // Transform response to LiveRangeDataItem array
        const responseData = response?.contents || response?.content || response || [];
        const dataArray = Array.isArray(responseData) ? responseData : [];

        // Map all items without filtering by employeeId/office (API already filters by these)
        const newLiveRangeData: LiveRangeDataItem[] = dataArray.map((item: any) => ({
          startDate: item.startDate,
          templateId: item.rangeNo,
          rangeNo: item.rangeNo || 0,
          employeeId: item.employeeId,
          office: item.office,
        }));
        // Merge new data with existing data (avoid duplicates by startDate)
        setLocalLiveRangeData((prevData) => {
          const existingDates = new Set(newLiveRangeData.map((item) => item.startDate));
          const filteredPrevData = prevData.filter((item) => !existingDates.has(item.startDate));
          const merged = [...filteredPrevData, ...newLiveRangeData];
          return merged;
        });
      } catch (error) {}
    },
    [isSpecialRange, mainInfoEdit.detailsData, localLiveRangeData]
  );

  // Initial fetch when detailsData becomes available with employeeNum and officeNum
  useEffect(() => {
    // Check for both possible field names
    const employeeId = mainInfoEdit.detailsData?.employeeNum || mainInfoEdit.detailsData?.employeeId;
    const office = mainInfoEdit.detailsData?.officeNum || mainInfoEdit.detailsData?.office;

    if (isSpecialRange && !initialFetchDone && employeeId && office) {
      setInitialFetchDone(true);
      // Fetch for current month
      fetchLiveRangeDataForMonth(moment());
    }
  }, [isSpecialRange, initialFetchDone, mainInfoEdit.detailsData]);

  // Initialize selectedCalendarDate when detailsData or liveRangeData is available
  useEffect(() => {
    if (isSpecialRange && selectedCalendarDate === null) {
      // Try to get startDate from detailsData first
      const detailsStartDate = mainInfoEdit.detailsData?.startDate;
      if (detailsStartDate) {
        const dateToSet = moment(detailsStartDate);
        setSelectedCalendarDate(dateToSet);
        setCalendarDisplayMonth(dateToSet.clone());
        // Fetch data for the initial month
        fetchLiveRangeDataForMonth(dateToSet);
      } else if (liveRangeData && liveRangeData.length > 0) {
        // Try to find the date matching the initialTemplateId
        if (initialTemplateId) {
          const matchingItem = liveRangeData.find((item) => String(item.templateId) === String(initialTemplateId));
          if (matchingItem) {
            const dateToSet = moment(matchingItem.startDate);
            setSelectedCalendarDate(dateToSet);
            setCalendarDisplayMonth(dateToSet.clone());
            // Fetch data for the initial month
            fetchLiveRangeDataForMonth(dateToSet);
            return;
          }
        }
        // Fallback to first item in liveRangeData
        const dateToSet = moment(liveRangeData[0].startDate);
        setSelectedCalendarDate(dateToSet);
        setCalendarDisplayMonth(dateToSet.clone());
        // Fetch data for the initial month
        fetchLiveRangeDataForMonth(dateToSet);
      }
    }
  }, [isSpecialRange, mainInfoEdit.detailsData?.startDate, liveRangeData, initialTemplateId]);

  if (isCreate) {
    const {
      detailsData,
      isSubmitDisabled,
      patternName,
      setPatternName,
      templateNameDirty,
      setTemplateNameDirty,
      templateNameValid,
      liveHooStart,
      liveHooEnd,
      pace,
      handleCreate,
      createError,
    } = mainInfoCreate;

    const isFormatInvalid = patternName && !/^(0?[1-9]|1[0-2])-(0?[1-9]|1[0-2])\s+.*/.test(patternName);

    return (
      <StyledInfoContainer>
        <StyledParamsForm
          form={form}
          layout='vertical'
          className='search-container'
          onValuesChange={(changedValues) => {
            if (changedValues.patternName !== undefined) {
              setPatternName(changedValues.patternName);
              setTemplateNameDirty(true);
            }
          }}
        >
          <StyledFormItemName
            name='patternName'
            label='Template name'
            rules={[
              { required: true, message: 'Please enter Template Name' },
              {
                pattern: /^(0?[1-9]|1[0-2])\s*-\s*(0?[1-9]|1[0-2])\s+.*/,
                message: 'Invalid template format.',
              },
            ]}
            validateStatus={templateNameValid === false || (templateNameDirty && isFormatInvalid) ? 'error' : undefined}
            help={
              templateNameValid === false ? (
                <div style={{ color: 'red', fontSize: 12 }}>{patternName} is already exists.</div>
              ) : templateNameDirty && isFormatInvalid ? (
                <div style={{ color: 'red', fontSize: 12 }}>Invalid template format.</div>
              ) : null
            }
          >
            <Input allowClear placeholder='Text here' />
          </StyledFormItemName>

          <StyledFormItem key='startAt' name='startAt' label='Day starts at'>
            <Input allowClear placeholder='08:00 AM' disabled />
          </StyledFormItem>
          <StyledFormItem key='templateDuration' name='templateDuration' label='Duration of the template (in hours)'>
            <Input readOnly style={{ width: '100%' }} disabled />
          </StyledFormItem>
          <StyledFormItem key='templateHOODuration' name='templateHOODuration' label='HOO Duration (in hours)'>
            <Input readOnly style={{ width: '100%' }} disabled />
          </StyledFormItem>
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}>
            <span style={{ fontWeight: 500, marginBottom: 2 }}>HOO Start: {liveHooStart}</span>
            <span style={{ fontWeight: 500 }}>HOO End: {liveHooEnd}</span>
          </div>
          <CornerFormItemsGroup>
            <StyledOverall>
              <span>Overall pace:</span>
              <Text strong>{typeof pace === 'number' && !isNaN(pace) ? pace : '-'} / h</Text>
            </StyledOverall>
            <StyledBtnGroup>
              <Space>
                {/* CLONE AND SAVE button removed in create mode */}
                {!(isSpecialRange || isRange0) && (
                  <>
                    <StyledPrimaryButton type='primary' disabled={isSubmitDisabled} onClick={() => handleCreate(true)}>
                      SAVE DRAFT
                    </StyledPrimaryButton>
                    <StyledPrimaryButton type='primary' disabled={isSubmitDisabled} onClick={() => handleCreate(false)}>
                      CREATE
                    </StyledPrimaryButton>
                  </>
                )}
              </Space>
            </StyledBtnGroup>
            {createError && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 2, textAlign: 'right' }}>{createError}</div>
            )}
          </CornerFormItemsGroup>
        </StyledParamsForm>
      </StyledInfoContainer>
    );
  } else {
    const {
      detailsData,
      isSubmitDisabled,
      hasUnsavedChanges,
      patternName,
      setPatternName,
      templateNameDirty,
      setTemplateNameDirty,
      templateNameValid,
      templateDuration,
      hooStart,
      hooEnd,
      onSubmit,
      handleUpdate,
      handlePublish,
      handleClone, // <-- expect from hook
      isCloneDisabled, // <-- expect from hook
    } = mainInfoEdit;
    // Disable UPDATE in MainInfo while a slot is being edited or a new slot is in creation
    const isEditingOrAdding = !!editedExam || !!addedExam;

    // Handler for switching dates in the calendar with unsaved changes check
    const handleDateSelect = (date: Moment) => {
      // Don't allow selecting past dates
      if (date.isBefore(moment(), 'day')) {
        return;
      }

      const dateStr = date.format('YYYY-MM-DD');
      const matchingRange = localLiveRangeData.find((item) => moment(item.startDate).format('YYYY-MM-DD') === dateStr);

      if (!matchingRange) {
        // No data for this date, don't switch
        return;
      }

      const switchToDate = () => {
        setSelectedCalendarDate(date);
        setCalendarDisplayMonth(date.clone());
        // Call fetchLiveRangeTemplateDetails with the matching range data
        dispatch(
          fetchLiveRangeTemplateDetails({
            id: matchingRange.templateId,
            rangeType: 'special',
            employeeNum: matchingRange.employeeId,
            officeNum: matchingRange.office,
            rangeNo: matchingRange.rangeNo,
            startDate: matchingRange.startDate,
          })
        );
      };

      // Check if there are unsaved changes
      if (hasUnsavedChanges) {
        // Store pending switch info and show modal
        setPendingSwitchDate({ date, matchingRange });
        setSwitchDateModalVisible(true);
      } else {
        // No unsaved changes, switch directly
        switchToDate();
      }
    };

    // Handlers for switch date confirmation modal
    const handleSwitchDateSaveAndSwitch = async () => {
      if (!pendingSwitchDate) return;
      setSwitchDateModalVisible(false);
      // Call handleUpdate with skipConfirmAndRedirect=true
      await handleUpdate(false, true);
      // After update, switch to the new date
      setSelectedCalendarDate(pendingSwitchDate.date);
      setCalendarDisplayMonth(pendingSwitchDate.date.clone());
      dispatch(
        fetchLiveRangeTemplateDetails({
          id: pendingSwitchDate.matchingRange.templateId,
          rangeType: 'special',
          employeeNum: pendingSwitchDate.matchingRange.employeeId,
          officeNum: pendingSwitchDate.matchingRange.office,
          rangeNo: pendingSwitchDate.matchingRange.rangeNo,
          startDate: pendingSwitchDate.matchingRange.startDate,
        })
      );
      setPendingSwitchDate(null);
    };

    const handleSwitchDateDiscardAndSwitch = () => {
      if (!pendingSwitchDate) return;
      setSwitchDateModalVisible(false);
      // Discard changes and switch
      setSelectedCalendarDate(pendingSwitchDate.date);
      setCalendarDisplayMonth(pendingSwitchDate.date.clone());
      dispatch(
        fetchLiveRangeTemplateDetails({
          id: pendingSwitchDate.matchingRange.templateId,
          rangeType: 'special',
          employeeNum: pendingSwitchDate.matchingRange.employeeId,
          officeNum: pendingSwitchDate.matchingRange.office,
          rangeNo: pendingSwitchDate.matchingRange.rangeNo,
          startDate: pendingSwitchDate.matchingRange.startDate,
        })
      );
      setPendingSwitchDate(null);
    };

    const handleSwitchDateCancel = () => {
      setSwitchDateModalVisible(false);
      setPendingSwitchDate(null);
    };

    return (
      <StyledInfoContainer showWatermark={isSpecialRange || isRange0}>
        <StyledParamsForm
          form={form}
          layout='vertical'
          className='search-container'
          onFinish={onSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.patternName !== undefined) {
              setPatternName(changedValues.patternName);
              setTemplateNameDirty(true);
            }
          }}
        >
          <StyledFormItemName
            name='patternName'
            label='Pattern name'
            rules={[{ required: true, message: 'Please enter Pattern Name' }]}
            validateStatus={templateNameValid === false ? 'error' : undefined}
            help={
              templateNameValid === false ? (
                <div style={{ color: 'red', fontSize: 12 }}>
                  {detailsData?.hooPart || ''} {patternName} is already exists.`
                </div>
              ) : null
            }
          >
            <Input allowClear placeholder='Text here' disabled={!!detailsData?.assigned} />
          </StyledFormItemName>

          <StyledFormItem key='startAt' name='startAt' label='Day starts at'>
            <Input allowClear placeholder='08:00 AM' disabled />
          </StyledFormItem>
          <StyledFormItem key='templateDuration' name='templateDuration' label='Duration of the template (in hours)'>
            <InputNumber placeholder='Enter duration' min={1} style={{ width: '100%' }} disabled />
          </StyledFormItem>
          {!isSpecialRange && (
            <StyledFormItem key='templateHOODuration' name='templateHOODuration' label='HOO Duration (in hours)'>
              <Input readOnly style={{ width: '100%' }} disabled />
            </StyledFormItem>
          )}
          {isRange0 && !isSpecialRange && (
            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}>
              <span style={{ fontWeight: 500, marginBottom: 2 }}>
                Provider: <br /> {detailsData?.provider}
              </span>
              <span style={{ fontWeight: 500 }}>
                Location: <br />
                {detailsData?.location}
              </span>
            </div>
          )}
          {isSpecialRange ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 24, marginBottom: 8 }}>
                {/* Left side - Calendar */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <StyledDateLabel>Toggle Live Range Day</StyledDateLabel>
                  <StyledCalendarWrapper>
                    <Calendar
                      key={`calendar-${calendarKey}`}
                      fullscreen={false}
                      value={calendarDisplayMonth}
                      onSelect={handleDateSelect}
                      dateFullCellRender={(date: Moment) => {
                        const dateStr = date.format('YYYY-MM-DD');
                        const isPastDate = date.isBefore(moment(), 'day');
                        // Direct string comparison for better reliability
                        const matchingItem = localLiveRangeData.find((item) => item.startDate === dateStr);
                        const hasData = !isPastDate && !!matchingItem;

                        const isSelected =
                          selectedCalendarDate && selectedCalendarDate.format('YYYY-MM-DD') === dateStr;
                        return (
                          <StyledCalendarCell hasData={hasData} isSelected={isSelected} isPastDate={isPastDate}>
                            {date.date()}
                          </StyledCalendarCell>
                        );
                      }}
                      headerRender={() => {
                        const monthName = calendarDisplayMonth.format('MMMM YYYY');
                        const isCurrentMonth = calendarDisplayMonth.isSame(moment(), 'month');
                        const onPrevMonth = () => {
                          if (!isCurrentMonth) {
                            const newMonth = calendarDisplayMonth.clone().subtract(1, 'month');
                            setCalendarDisplayMonth(newMonth);
                            fetchLiveRangeDataForMonth(newMonth);
                          }
                        };
                        const onNextMonth = () => {
                          const newMonth = calendarDisplayMonth.clone().add(1, 'month');
                          setCalendarDisplayMonth(newMonth);
                          fetchLiveRangeDataForMonth(newMonth);
                        };
                        return (
                          <StyledCalendarHeader>
                            <Button
                              type='text'
                              size='small'
                              icon={<LeftOutlined />}
                              onClick={onPrevMonth}
                              disabled={isCurrentMonth}
                            />
                            <span>{monthName}</span>
                            <Button type='text' size='small' icon={<RightOutlined />} onClick={onNextMonth} />
                          </StyledCalendarHeader>
                        );
                      }}
                    />
                  </StyledCalendarWrapper>
                </div>
                {/* Right side - HOO info, pace, and button */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1 }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <span style={{ fontWeight: 500 }}>
                      HOO Duration: {detailsData?.templateHOODuration || '-'} hours
                    </span>
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <span style={{ fontWeight: 500 }}>Provider: {detailsData?.provider || '-'}</span>
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <span style={{ fontWeight: 500 }}>Location: {detailsData?.location || '-'}</span>
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <span style={{ fontWeight: 500 }}>HOO Start: {hooStart}</span>
                  </p>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <span style={{ fontWeight: 500 }}>HOO End: {hooEnd}</span>
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 16 }}>
                    <span>Overall pace:</span>
                    <Text strong style={{ marginLeft: 8 }}>
                      {detailsData?.pace || '-'} / h
                    </Text>
                  </div>
                  <div>
                    <Tooltip
                      placement='top'
                      title={isEditingOrAdding ? 'Finish or cancel the open edit before updating the template.' : null}
                    >
                      <span>
                        <Space direction='vertical' size='small'>
                          {isClone && (
                            <StyledPrimaryButton type='primary' disabled={isCloneDisabled} onClick={handleClone}>
                              CLONE AND SAVE
                            </StyledPrimaryButton>
                          )}
                          <StyledPrimaryButton
                            type='primary'
                            disabled={isSubmitDisabled || isEditingOrAdding}
                            onClick={() => handleUpdate(false)}
                          >
                            UPDATE
                          </StyledPrimaryButton>
                        </Space>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}>
                <span style={{ fontWeight: 500, marginBottom: 2 }}>HOO Start: {hooStart}</span>
                <span style={{ fontWeight: 500 }}>HOO End: {hooEnd}</span>
              </div>
              <CornerFormItemsGroup>
                <StyledOverall>
                  <span>Overall pace:</span>
                  <Text strong>{detailsData?.pace || '-'} / h</Text>
                </StyledOverall>
                <StyledBtnGroup>
                  <Tooltip
                    placement='top'
                    title={isEditingOrAdding ? 'Finish or cancel the open edit before updating the template.' : null}
                  >
                    <span>
                      <Space>
                        {isClone && (
                          <StyledPrimaryButton type='primary' disabled={isCloneDisabled} onClick={handleClone}>
                            CLONE AND SAVE
                          </StyledPrimaryButton>
                        )}
                        {isDraft ? (
                          <Space>
                            <StyledPrimaryButton type='primary' onClick={() => handlePublish()}>
                              PUBLISH
                            </StyledPrimaryButton>
                            <StyledPrimaryButton
                              type='primary'
                              disabled={isSubmitDisabled || isEditingOrAdding}
                              onClick={() => handleUpdate(true)}
                            >
                              UPDATE DRAFT
                            </StyledPrimaryButton>
                          </Space>
                        ) : (
                          <StyledPrimaryButton
                            type='primary'
                            disabled={isSubmitDisabled || isEditingOrAdding}
                            onClick={() => handleUpdate(false)}
                          >
                            UPDATE
                          </StyledPrimaryButton>
                        )}
                      </Space>
                    </span>
                  </Tooltip>
                </StyledBtnGroup>
              </CornerFormItemsGroup>
            </>
          )}
        </StyledParamsForm>

        {/* Switch Date Confirmation Modal */}
        <StyledConfirmModal
          title={
            <>
              <ExclamationCircleOutlined /> <span className={'confirm-title'}>Confirmation</span>
            </>
          }
          visible={switchDateModalVisible}
          destroyOnClose
          centered
          width={520}
          footer={
            <>
              <Button type={'default'} onClick={handleSwitchDateCancel}>
                Cancel
              </Button>
              <Button type={'default'} onClick={handleSwitchDateDiscardAndSwitch}>
                Discard & Switch
              </Button>
              <Button type={'primary'} onClick={handleSwitchDateSaveAndSwitch}>
                Save & Switch
              </Button>
            </>
          }
        >
          <span>{`You have unsaved provider's Live Range changes for date ${
            selectedCalendarDate ? moment(selectedCalendarDate).format('MMM-DD') : ''
          }. Do you want to save them before switching to another date? This action can't be undone.`}</span>
        </StyledConfirmModal>
      </StyledInfoContainer>
    );
  }
};

const StyledInfoContainer = styled.div<{ showWatermark?: boolean }>`
  width: 100%;
  background: #fff;
  margin-bottom: 20px;
  margin-right: 35px;
  padding: 15px 20px 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.showWatermark &&
    `
    &::before {
      content: 'Live Ranges Management';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-45deg);
      font-size: 36px;
      font-weight: 800;
      color: rgba(0, 0, 0, 0.08);
      white-space: nowrap;
      pointer-events: none;
      z-index: 100000;
    }
    > * {
      position: relative;
      z-index: 1;
    }
  `}
`;

const CornerFormItemsGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledOverall = styled.div`
  width: 140px;
  display: flex;
  justify-content: space-between;
  align-self: flex-end;
  margin-bottom: 10px;
`;

const StyledBtnGroup = styled.div`
  align-self: flex-end;
  display: flex;

  > :first-child {
    margin-right: 10px;
  }

  &&& .ant-form-item {
    margin: 0;
  }
`;

const StyledCalendarRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 16px;
  align-items: flex-start;
`;

const StyledCalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const StyledCalendarWrapper = styled.div`
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 2px;
  width: fit-content;

  .ant-picker-calendar-mini .ant-picker-cell-inner {
    height: 16px !important;
    min-height: 16px !important;
    line-height: 16px !important;
  }

  .ant-picker-calendar-mini td {
    padding: 0px !important;
  }
  .ant-picker-calendar {
    .ant-picker-panel {
      border-top: none;
    }
    .ant-picker-calendar-header {
      display: none;
    }
    .ant-picker-body {
      padding: 4px !important;
    }
    .ant-picker-content {
      width: auto;
      /* Hide dates from other months */
      .ant-picker-cell:not(.ant-picker-cell-in-view) {
        visibility: hidden;
        pointer-events: none;
      }

      tbody tr {
        height: 20px !important;
      }
      tbody tr:last-child {
        display: none;
      }
    }
  }
`;

const StyledInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  min-width: 150px;
`;

const StyledDateLabel = styled.span`
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
`;

const StyledCalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 4px;
  margin-bottom: 4px;

  span {
    font-weight: 500;
    font-size: 13px;
  }

  button {
    padding: 0 4px;
  }
`;

const StyledCalendarCell = styled.div<{ hasData?: boolean; isSelected?: boolean; isPastDate?: boolean }>`
  width: 18px;
  height: 18px;
  line-height: 18px !important;
  font-size: 10px;
  text-align: center;
  border-radius: 2px;
  cursor: ${(props) => (props.isPastDate ? 'not-allowed' : props.hasData ? 'pointer' : 'default')};
  background-color: ${(props) => {
    if (props.isPastDate) return '#f5f5f5';
    if (props.isSelected) return '#1890ff';
    if (props.hasData) return '#e6f7ff';
    return 'transparent';
  }};
  color: ${(props) => {
    if (props.isPastDate) return '#d9d9d9';
    if (props.isSelected) return '#fff';
    if (props.hasData) return '#1890ff';
    return 'inherit';
  }};
  border: ${(props) => (props.hasData && !props.isSelected && !props.isPastDate ? '1px solid #91d5ff' : 'none')};
  font-weight: ${(props) => (props.hasData && !props.isPastDate ? '600' : 'normal')};

  &:hover {
    background-color: ${(props) => {
      if (props.isPastDate) return '#f5f5f5';
      if (props.isSelected) return '#1890ff';
      if (props.hasData) return '#bae7ff';
      return 'transparent';
    }};
  }
`;

const StyledConfirmModal = styled(Modal)`
  .ant-modal-content {
    width: initial;
    border-radius: 2px;

    .ant-modal-close {
      display: none;
    }

    .ant-modal-header {
      padding: 16px 24px;
      background: #fff;
      border-bottom: 1px solid #f0f0f0;
      border-radius: 2px;

      .ant-modal-title {
        font-size: 15px;
        color: #ff991f;

        .confirm-title {
          color: #000;
          margin-left: 5px;
        }
      }
    }
  }
`;
